import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, ArrowLeft, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const QR_URL = process.env.REACT_APP_QR_TARGET_URL;

const QRCodePage = () => {
  const qrRef = useRef(null);

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = 'queensland-ranch-qr.png';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const printQR = () => {
    window.print();
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden bg-gradient-to-b from-[#0B1224] via-navy to-[#0B1224] print:bg-white"
      data-testid="qr-page"
    >
      {/* Ambient gold glow - hidden on print */}
      <div className="absolute inset-0 pointer-events-none print:hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[radial-gradient(ellipse,rgba(184,155,92,0.15)_0%,transparent_60%)]" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[radial-gradient(ellipse,rgba(184,155,92,0.10)_0%,transparent_60%)]" />
      </div>

      {/* Back button - hidden on print */}
      <div className="w-full max-w-2xl mb-8 print:hidden relative z-10">
        <Link to="/">
          <Button
            variant="ghost"
            className="text-gold/80 hover:text-gold hover:bg-white/5"
            data-testid="qr-back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Printable luxury card */}
      <div
        className="relative bg-gradient-to-b from-[#0B1224] via-navy to-[#0B1224] rounded-2xl shadow-[0_20px_80px_rgba(0,0,0,0.6)] border border-gold/40 p-10 md:p-16 max-w-2xl w-full text-center print:shadow-none print:border-gold print:bg-white print:text-navy"
        data-testid="qr-card"
      >
        {/* Gold corner flourishes */}
        <div className="absolute top-5 left-5 w-14 h-14 border-l-2 border-t-2 border-gold/70" aria-hidden="true" />
        <div className="absolute top-5 right-5 w-14 h-14 border-r-2 border-t-2 border-gold/70" aria-hidden="true" />
        <div className="absolute bottom-5 left-5 w-14 h-14 border-l-2 border-b-2 border-gold/70" aria-hidden="true" />
        <div className="absolute bottom-5 right-5 w-14 h-14 border-r-2 border-b-2 border-gold/70" aria-hidden="true" />

        <div className="relative z-10">
          {/* Brand name */}
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-gold mb-3 tracking-[0.3em] uppercase print:text-navy">
            Queensland Ranch
          </h1>

          {/* Gold divider */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-14 bg-gradient-to-r from-transparent to-gold" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold" />
            <div className="h-px w-14 bg-gradient-to-l from-transparent to-gold" />
          </div>

          {/* Positioning line (matches homepage) */}
          <p className="font-serif text-lg md:text-xl italic text-white/90 mb-1 print:text-navy">
            Private Luxury Estate
          </p>
          <p className="font-serif text-lg md:text-xl italic text-white/90 mb-10 print:text-navy">
            for Unforgettable Weddings
          </p>

          {/* QR code with gold frame and shadow */}
          <div className="flex justify-center mb-8">
            <div
              ref={qrRef}
              className="relative p-5 bg-white rounded-lg border-[3px] border-gold shadow-[0_12px_40px_rgba(184,155,92,0.25)] print:shadow-none"
              data-testid="qr-code-wrapper"
            >
              <QRCodeCanvas
                value={QR_URL}
                size={280}
                level="H"
                includeMargin={false}
                fgColor="#1A243A"
                bgColor="#FFFFFF"
              />
            </div>
          </div>

          {/* CTA + Urgency */}
          <p className="text-gold text-xl md:text-2xl font-serif italic mb-3 tracking-wide print:text-navy">
            Scan to Check Availability
          </p>
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold/15 border border-gold/50 print:bg-navy print:border-navy">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse print:bg-white" />
            <p className="text-gold text-xs md:text-sm font-semibold tracking-[0.25em] uppercase print:text-white">
              Limited 2026 Dates Available
            </p>
          </div>

          {/* Website URL - subtle */}
          <p className="text-white/50 text-xs tracking-[0.3em] uppercase mt-8 print:text-navy/60">
            queenslandranch.com
          </p>
        </div>
      </div>

      {/* Actions - hidden on print */}
      <div className="flex flex-col sm:flex-row gap-4 mt-10 print:hidden relative z-10">
        <Button
          onClick={downloadQR}
          size="lg"
          className="bg-gold hover:bg-gold/90 text-navy font-semibold px-8 tracking-wide shadow-[0_8px_30px_rgba(184,155,92,0.4)]"
          data-testid="qr-download-button"
        >
          <Download className="mr-2 h-5 w-5" />
          Download PNG
        </Button>
        <Button
          onClick={printQR}
          size="lg"
          variant="outline"
          className="bg-transparent border-gold/60 text-gold hover:bg-gold/10 hover:border-gold hover:text-gold px-8 tracking-wide"
          data-testid="qr-print-button"
        >
          <Printer className="mr-2 h-5 w-5" />
          Print
        </Button>
      </div>

      <p className="text-white/50 text-sm mt-8 max-w-md text-center print:hidden italic relative z-10">
        Download or print for flyers, business cards, and event signage.
      </p>
    </div>
  );
};

export default QRCodePage;
